import type { LineManagerType } from '@libs/data-access/entities'
import { membershipService, requestOptions } from '@libs/data-access/queries'
import type { SelectOption } from '@libs/ui/ds'
import {
  Flex,
  IconWrap,
  InformationCircleIcon,
  Text,
  Tooltip
} from '@libs/ui/ds'
import { Controller } from '@libs/ui/form'
import { useMembershipPageTranslation } from '@libs/utils/translations'
import type { MemberViewSubviewRouteParams } from '@spa/core/router'
import type { LineManagerFormType } from '@spa/scenes/MemberPage/modals/ContractInfoFormModal/Forms/ContractForm/components/LineManager/types'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

type Props = {
  locationId?: number
  teamId?: number
  lineManager?: LineManagerType
  disabled?: boolean
}

export const LineManager: FC<Props> = ({
  locationId,
  teamId,
  lineManager,
  disabled = false
}) => {
  const { t } = useMembershipPageTranslation()
  const { membership_id } = useParams<MemberViewSubviewRouteParams>()

  const defaultManager = lineManager?.id
    ? `${lineManager?.firstname} ${lineManager?.lastname}`
    : ''

  const [search, setSearch] = useState(defaultManager || '')

  const [options, setOptions] = useState<SelectOption[]>(
    lineManager?.id
      ? [
          {
            label: defaultManager,
            value: lineManager?.id.toString()
          }
        ]
      : []
  )

  const { control } = useFormContext<LineManagerFormType>()

  const lineManagers = membershipService.lineManagers.useGet({
    queryParams: {
      per_page: requestOptions.PAGINATION_DEFAULT_PER_PAGE_LARGE,
      ...(locationId && { location_ids: [locationId] }),
      ...(teamId && { team_ids: [teamId] }),
      ...(membership_id && {
        for_membership_id: Number(membership_id)
      }),
      search
    }
  })

  useEffect(() => {
    setOptions(lineManagers?.data?.options || [])
  }, [lineManagers?.data?.options])

  return (
    <Controller.AsyncSelect
      clearable
      control={control}
      name='line_manager_id'
      loading={lineManagers.isLoading}
      dropdownWidth='target'
      dropdownPosition='top-start'
      disabled={!lineManagers.isSuccess || disabled}
      label={
        <Flex row className='gap-2'>
          <Text>{t('contract.info.form.contract.lineManager.label')}</Text>
          <Tooltip label={t('contract.info.form.contract.lineManager.tooltip')}>
            <IconWrap icon={<InformationCircleIcon />} />
          </Tooltip>
        </Flex>
      }
      options={options}
      defaultValue={defaultManager}
      debouncedSearchDelay={500}
      onDebouncedSearchChange={setSearch}
      placeholder={t('contract.info.form.contract.lineManager.placeholder')}
    />
  )
}
